import { Form, FormInstance } from 'antd';
import { useCallback } from 'react';

import { COMPANY_URLS } from '@/constants/urls';
import { useSelectScrollSearch } from '@/hooks/useSelectScrollSearch';
import { PaymentMethod, PaymentMethodTypeLabels } from '@/model/General/PaymentMethod';
import { Technician } from '@/model/General/Technician';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

/**
 * Hook que gestiona la selección y creación de métodos de pago
 * del técnico para el formulario de orden de servicio.
 */
const usePaymentMethods = (
  form: FormInstance | undefined,
  selectedTechnician: Technician | null,
) => {
  const { companyRepository } = useRepositoriesContext();
  const technicianPay = Form.useWatch('technician_pay', form);
  const {
    selectProps: technicianPaymentMethodsSelectProps,
    refetchData: refetchTechnicianPaymentMethods,
  } = useSelectScrollSearch(
    {
      resource: COMPANY_URLS.PAYMENT_METHOD_RESOURCE,
      optionLabel: (item: any) =>
        `${PaymentMethodTypeLabels[item.tipo]} - ${item.descripcion ?? ''}`,
      optionValue: (item: any) => item.id,
      queryOptions: {
        enabled: !!selectedTechnician?.empresa && !!technicianPay,
      },
      filters: [
        {
          value: selectedTechnician?.empresa,
          operator: 'eq' as const,
          field: 'empresa',
        },
      ],
    },
    {
      saveItem: true,
    },
  );
  const createPaymentMethod = useCallback(
    (paymentMethodData: Partial<PaymentMethod>) => {
      if (!selectedTechnician?.empresa) return;
      void companyRepository
        .createPaymentMethod({ ...paymentMethodData, empresa: selectedTechnician.empresa })
        .then(() => refetchTechnicianPaymentMethods());
    },
    [companyRepository, refetchTechnicianPaymentMethods, selectedTechnician],
  );

  return { technicianPaymentMethodsSelectProps, createPaymentMethod };
};

export default usePaymentMethods;
